import { useState, useEffect } from 'react';
import { getSalesReport } from '../utils/api';
import ExportButton from '../components/ExportButton';
import { formatCurrency, formatDateShort, getStatusBadgeClass } from '../utils/format';

const MONTHS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const PRODUCT_COLORS = {
  roof:       '#b4c5e0',
  coating:    '#ADD8E6',
  windows:    '#FF5050',
  insulation: '#CC66FF',
  gutters:    '#FFE0B2',
  patio:      '#FFF9C4',
  fencing:    '#F8BBD0',
};

function getProductColor(service) {
  if (!service) return '#CC66FF';
  const s = service.toLowerCase();
  for (const [key, color] of Object.entries(PRODUCT_COLORS)) {
    if (s.includes(key)) return color;
  }
  return '#CC66FF';
}

export default function SalesReport() {
  const now = new Date();
  const [data, setData]   = useState(null);
  const [year, setYear]   = useState(now.getFullYear().toString());
  const [month, setMonth] = useState(String(now.getMonth() + 1).padStart(2, '0'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getSalesReport(year, month).then(setData).finally(() => setLoading(false));
  }, [year, month]);

  const activeJobs    = data?.jobs?.filter(j => (j.sales_status||'').toLowerCase() !== 'cancelled') || [];
  const cancelledJobs = data?.jobs?.filter(j => (j.sales_status||'').toLowerCase() === 'cancelled') || [];
  const allJobs       = [...activeJobs, ...cancelledJobs];

  const totalJobs = data?.jobs?.length || 0;
  const totalVal  = data?.jobs?.reduce((s,j) => s + parseFloat(j.change_order_amt||j.contract_amt||0), 0) || 0;
  const exclCount = activeJobs.length;
  const exclVal   = activeJobs.reduce((s,j) => s + parseFloat(j.change_order_amt||j.contract_amt||0), 0);

  const monthLabel = MONTHS.find(m => m.value === month)?.label || '';

  const PrintIcon = () => (
    <svg viewBox="0 0 20 20" fill="currentColor" style={{width:15,height:15}}>
      <path fillRule="evenodd" d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a1 1 0 001 1h8a1 1 0 001-1v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a1 1 0 00-1-1H6a1 1 0 00-1 1zm2 0h6v3H7V4zm-1 9h8v4H6v-4zm-2-4a1 1 0 100 2 1 1 0 000-2z" clipRule="evenodd"/>
    </svg>
  );

  return (
    <>
      <div className="page-header">
        <h2>Sales Report</h2>
        <div className="page-header-actions">
          <select className="form-select" style={{ width: 130 }} value={month} onChange={e => setMonth(e.target.value)}>
            {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <select className="form-select" style={{ width: 90 }} value={year} onChange={e => setYear(e.target.value)}>
            {(data?.availableYears || [year]).map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button className="btn btn-outline btn-sm" onClick={() => window.print()}>
            <PrintIcon /> Print
          </button>
          <ExportButton sheet='sales' year={year} />
        </div>
      </div>

      <div className="page-body">
        {loading ? (
          <div className="loader"><div className="spinner" /></div>
        ) : !data ? null : (
          <>
            {/* ── SCREEN VIEW ── */}
            <div className="screen-only">
              {/* Summary cards */}
              <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
                <div className="stat-card" style={{ minWidth:160 }}>
                  <div className="stat-label">Total Jobs</div>
                  <div className="stat-value">{totalJobs}</div>
                  <div className="stat-sub">{formatCurrency(totalVal)}</div>
                </div>
                <div className="stat-card" style={{ minWidth:160 }}>
                  <div className="stat-label">Excl. Cancelled</div>
                  <div className="stat-value">{exclCount}</div>
                  <div className="stat-sub">{formatCurrency(exclVal)}</div>
                </div>
                {data.repSummary && data.repSummary.map(rep => (
                  <div key={rep.sales_rep} className="stat-card gold" style={{ minWidth:140 }}>
                    <div className="stat-label">{rep.sales_rep || 'Unknown'}</div>
                    <div className="stat-value" style={{ fontSize:'1.5rem' }}>{rep.total_jobs}</div>
                    <div className="stat-sub">{formatCurrency(rep.total_value)}</div>
                  </div>
                ))}
              </div>

              {/* Job list table */}
              <div className="card">
                <div className="card-header">
                  <h3>{monthLabel} {year} — Monthly Sales Report</h3>
                </div>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>PO #</th>
                        <th>Date Sold</th>
                        <th>Customer Name</th>
                        <th>Sales Rep</th>
                        <th>Service Type</th>
                        <th>Sales Status</th>
                        <th>Contract Amt</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allJobs.length === 0 ? (
                        <tr><td colSpan={8} style={{ textAlign:'center', padding:24, color:'#aaa' }}>No jobs found for {monthLabel} {year}</td></tr>
                      ) : allJobs.map((job, idx) => {
                        const isCancelled = (job.sales_status||'').toLowerCase() === 'cancelled';
                        return (
                          <tr key={job.po_number} style={{ background: isCancelled ? '#FFC7C7' : idx % 2 === 0 ? '#e8edf5' : '#fff' }}>
                            <td className="td-po">{job.po_number}</td>
                            <td>{formatDateShort(job.contract_date)}</td>
                            <td className="td-name">{job.customer_name}</td>
                            <td>{job.sales_rep}</td>
                            <td>{job.service}</td>
                            <td><span className={getStatusBadgeClass(job.sales_status)}>{job.sales_status}</span></td>
                            <td className="td-amt">{formatCurrency(job.change_order_amt || job.contract_amt)}</td>
                            <td style={{ fontSize:'0.78rem', color:'var(--gray-500)' }}>{job.notes}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* ── PRINT VIEW ── */}
            <div className="print-only">
              <div style={{ marginBottom:8, borderBottom:'2px solid #0d1f3b', paddingBottom:4, display:'flex', justifyContent:'space-between', alignItems:'flex-end' }}>
                <h2 style={{ fontFamily:'Georgia,serif', color:'#0d1f3b', fontSize:'14px', margin:0 }}>Amanda's Home Improvement — {monthLabel} {year} Sales Report</h2>
                <p style={{ fontSize:'8px', color:'#555', margin:0 }}>Printed: {new Date().toLocaleDateString()}</p>
              </div>

              <div style={{ display:'flex', gap:16, marginBottom:8, fontSize:'9px' }}>
                <span style={{ background:'#F57F17', color:'#fff', padding:'2px 8px', borderRadius:3, fontWeight:700 }}>Total Jobs: {totalJobs}</span>
                <span style={{ background:'#F57F17', color:'#fff', padding:'2px 8px', borderRadius:3, fontWeight:700 }}>Total Value: {formatCurrency(totalVal)}</span>
                <span style={{ padding:'2px 8px', borderRadius:3, border:'1px solid #ccc' }}>Excl. Cancelled: {exclCount} — {formatCurrency(exclVal)}</span>
              </div>

              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'8px' }}>
                <thead>
                  <tr style={{ background:'#1a355f', color:'#fff', WebkitPrintColorAdjust:'exact', printColorAdjust:'exact' }}>
                    <th style={{ padding:'4px 5px', textAlign:'left', whiteSpace:'nowrap' }}>PO #</th>
                    <th style={{ padding:'4px 5px', textAlign:'left', whiteSpace:'nowrap' }}>Date Sold</th>
                    <th style={{ padding:'4px 5px', textAlign:'left' }}>Customer Name</th>
                    <th style={{ padding:'4px 5px', textAlign:'left' }}>Sales Rep</th>
                    <th style={{ padding:'4px 5px', textAlign:'left' }}>Service</th>
                    <th style={{ padding:'4px 5px', textAlign:'left' }}>Status</th>
                    <th style={{ padding:'4px 5px', textAlign:'right', whiteSpace:'nowrap' }}>Contract Amt</th>
                    <th style={{ padding:'4px 5px', textAlign:'left' }}>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {allJobs.map((job, idx) => {
                    const isCancelled = (job.sales_status||'').toLowerCase() === 'cancelled';
                    const bg = isCancelled ? '#FFC7C7' : idx % 2 === 0 ? '#e8edf5' : '#ffffff';
                    return (
                      <tr key={job.po_number} style={{ background:bg, WebkitPrintColorAdjust:'exact', printColorAdjust:'exact', pageBreakInside:'avoid' }}>
                        <td style={{ padding:'3px 5px', fontWeight:700, borderBottom:'1px solid #ddd', whiteSpace:'nowrap' }}>{job.po_number}</td>
                        <td style={{ padding:'3px 5px', borderBottom:'1px solid #ddd', whiteSpace:'nowrap' }}>{formatDateShort(job.contract_date)}</td>
                        <td style={{ padding:'3px 5px', borderBottom:'1px solid #ddd' }}>{job.customer_name}</td>
                        <td style={{ padding:'3px 5px', borderBottom:'1px solid #ddd' }}>{job.sales_rep}</td>
                        <td style={{ padding:'3px 5px', borderBottom:'1px solid #ddd' }}>{job.service}</td>
                        <td style={{ padding:'3px 5px', borderBottom:'1px solid #ddd' }}>{job.sales_status}</td>
                        <td style={{ padding:'3px 5px', borderBottom:'1px solid #ddd', textAlign:'right', whiteSpace:'nowrap' }}>{formatCurrency(job.change_order_amt || job.contract_amt)}</td>
                        <td style={{ padding:'3px 5px', borderBottom:'1px solid #ddd', fontSize:'7px', color:'#555' }}>{job.notes}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </>
  );
}
